/**
 * Iter 67bb — Admin-side platform billing panel.
 *
 * Lives inside the Admin Dashboard. Lets admins:
 *   • Auto-generate a vendor's monthly platform invoice (subscription +
 *     commission + refund debt rolled into one)
 *   • Issue an ad-hoc invoice (compliance fine, damage dispute, etc.)
 *   • Mark invoices paid / cancel
 *   • Filter the master list by vendor and status
 *
 * Pairs with Iter 67ba's vendor rental invoice panel — both render the same
 * line-item shape so the UI stays consistent.
 */
import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Wand2, CheckCircle2, XCircle, Loader2, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";
import Money from "@/components/Money";
import InvoiceDeliveryActions from "@/components/invoices/InvoiceDeliveryActions";
import AttributionChips from "@/components/AttributionChips";

const STATUS_TONES = {
  draft:     "bg-neutral-100 text-neutral-700",
  issued:    "bg-blue-50 text-blue-700",
  overdue:   "bg-amber-50 text-amber-800",
  paid:      "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

function _monthBounds(d = new Date()) {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const start = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
  const end = new Date(Date.UTC(y, m + 1, 0)).toISOString().slice(0, 10);
  return { start, end };
}

export default function AdminPlatformInvoicesPanel() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filterVendor, setFilterVendor] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [autoOpen, setAutoOpen] = useState(false);
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const { start: monthStart, end: monthEnd } = _monthBounds();

  const [autoForm, setAutoForm] = useState({
    vendor_id: "",
    period_start: monthStart,
    period_end: monthEnd,
    include_subscription: true,
    include_commission: true,
    include_refund_debt: true,
    notes: "",
  });
  const [adhocForm, setAdhocForm] = useState({
    vendor_id: "",
    notes: "",
    line_items: [{ kind: "adhoc", label: "", qty: 1, unit_price: 0, vat_rate: 0 }],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [invR, vR] = await Promise.all([
        api.get("/admin/vendor-invoices", { params: { limit: 200 } }),
        api.get("/admin/vendors").catch(() => ({ data: [] })),
      ]);
      setItems(invR.data.items || []);
      // /admin/vendors returns the raw array.
      setVendors(Array.isArray(vR.data) ? vR.data : (vR.data?.items || []));
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load invoices.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return (items || []).filter((it) => {
      if (filterVendor !== "all" && it.vendor_id !== filterVendor) return false;
      if (filterStatus !== "all" && it.status !== filterStatus) return false;
      return true;
    });
  }, [items, filterVendor, filterStatus]);

  const totals = useMemo(() => {
    let outstanding = 0, paid = 0;
    for (const it of filtered) {
      const t = Number(it.totals?.total_gross || 0);
      if (it.status === "paid") paid += t;
      else if (["issued", "overdue"].includes(it.status)) outstanding += t;
    }
    return { outstanding: Math.round(outstanding * 100) / 100, paid: Math.round(paid * 100) / 100 };
  }, [filtered]);

  const onAutoGenerate = async () => {
    if (!autoForm.vendor_id) {
      toast.error("Pick a vendor first."); return;
    }
    setBusy(true);
    try {
      await api.post("/admin/vendor-invoices/auto-generate", autoForm);
      toast.success("Platform invoice generated.");
      setAutoOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not generate invoice.");
    } finally {
      setBusy(false);
    }
  };

  const onAdhocCreate = async () => {
    if (!adhocForm.vendor_id || !adhocForm.line_items.some((li) => li.label && li.unit_price)) {
      toast.error("Vendor + at least one line item required."); return;
    }
    setBusy(true);
    try {
      await api.post("/admin/vendor-invoices/adhoc", {
        vendor_id: adhocForm.vendor_id,
        notes: adhocForm.notes || null,
        line_items: adhocForm.line_items.map((li) => ({
          ...li, qty: Number(li.qty || 0),
          unit_price: Number(li.unit_price || 0),
          vat_rate: Number(li.vat_rate || 0),
        })),
      });
      toast.success("Ad-hoc invoice issued.");
      setAdhocOpen(false);
      setAdhocForm({ vendor_id: "", notes: "", line_items: [{ kind: "adhoc", label: "", qty: 1, unit_price: 0, vat_rate: 0 }] });
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not create invoice.");
    } finally {
      setBusy(false);
    }
  };

  const onMarkPaid = async (id) => {
    try {
      await api.post(`/admin/vendor-invoices/${id}/mark-paid`);
      toast.success("Marked paid.");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not mark paid.");
    }
  };
  const onCancel = async (id) => {
    if (!window.confirm("Cancel this invoice?")) return;
    try {
      await api.post(`/admin/vendor-invoices/${id}/cancel`);
      toast.success("Invoice cancelled.");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not cancel.");
    }
  };

  // Iter 67ez — admin queues a B2B easyInvoice fiscalization job on the vendor's relay agent.
  const onFiscalize = async (id) => {
    try {
      await api.post(`/admin/platform-invoices/${id}/fiscalize`);
      toast.success("Fiscalization job queued — vendor's relay agent will process it.");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not queue fiscalization.");
    }
  };

  const onAddAdhocLine = () => setAdhocForm((f) => ({
    ...f, line_items: [...f.line_items, { kind: "adhoc", label: "", qty: 1, unit_price: 0, vat_rate: 0 }],
  }));
  const onRemoveAdhocLine = (idx) => setAdhocForm((f) => ({
    ...f, line_items: f.line_items.filter((_, i) => i !== idx),
  }));
  const onAdhocLineChange = (idx, key, val) => setAdhocForm((f) => ({
    ...f, line_items: f.line_items.map((it, i) => i === idx ? { ...it, [key]: val } : it),
  }));

  return (
    <div className="space-y-6" data-testid="admin-platform-invoices-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-medium">Platform billing</h2>
          <p className="text-sm text-neutral-600 mt-1">Subscription · commission · refund debt · ad-hoc charges. Recovers from vendor payouts.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setAutoOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="platform-auto-generate-btn">
            <Wand2 className="h-4 w-4 mr-1.5" /> Auto-generate
          </Button>
          <Button onClick={() => setAdhocOpen(true)} variant="outline" data-testid="platform-adhoc-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Ad-hoc invoice
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-900">Outstanding</div>
          <div className="font-display text-2xl font-semibold text-amber-900 mt-1"><Money amount={totals.outstanding} /></div>
        </div>
        <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-900">Paid</div>
          <div className="font-display text-2xl font-semibold text-emerald-900 mt-1"><Money amount={totals.paid} /></div>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <Select value={filterVendor} onValueChange={setFilterVendor}>
          <SelectTrigger className="w-64" data-testid="platform-filter-vendor"><SelectValue placeholder="All vendors" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.business_name || v.name || v.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48" data-testid="platform-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="issued">Issued</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-border text-[10px] uppercase tracking-wider text-neutral-600">
              <tr>
                <th className="text-left px-4 py-3">Number</th>
                <th className="text-left px-4 py-3">Vendor</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Period / Issued</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-500">No invoices match the current filters.</td></tr>}
              {!loading && filtered.map((it) => (
                <tr key={it.id} className="border-b border-border last:border-0 hover:bg-neutral-50/60" data-testid={`platform-invoice-row-${it.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-neutral-900">{it.invoice_number}</div>
                    <AttributionChips entity={it} actions={["create", "update", "approve"]} compact className="mt-1" />
                  </td>
                  <td className="px-4 py-3 text-sm">{it.vendor_name || it.vendor_id?.slice(0, 8)}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={`uppercase tracking-wider text-[10px] font-bold ${STATUS_TONES[it.status] || STATUS_TONES.draft}`}>{it.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-600">
                    {it.ad_hoc ? "Ad-hoc" : "Auto"}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-600">
                    {it.period_start ? `${it.period_start.slice(0, 10)} → ${it.period_end?.slice(0, 10)}` : it.issued_at?.slice(0, 10)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold"><Money amount={it.totals?.total_gross || 0} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      <InvoiceDeliveryActions
                        kind="platform"
                        invoiceId={it.id}
                        invoiceNumber={it.invoice_number}
                        variant="icons"
                      />
                      {it.status === "issued" && (
                        <>
                          <Button variant="ghost" size="sm" className="text-emerald-700" onClick={() => onMarkPaid(it.id)} data-testid={`platform-mark-paid-${it.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Paid
                          </Button>
                          <Button variant="ghost" size="sm" className="text-rose-700" onClick={() => onCancel(it.id)} data-testid={`platform-cancel-${it.id}`}>
                            <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                      {/* Iter 67ez — Albania B2B easyInvoice fiscalization */}
                      {it.fiscal_status === "succeeded" ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold"
                          title={`NIVF ${it.fiscal_nivf || ""} · NSLF ${it.fiscal_nslf || ""}`}
                          data-testid={`platform-fiscalized-${it.id}`}
                        >
                          <Receipt className="h-3 w-3" /> Fiscalized
                        </span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-700"
                          onClick={() => onFiscalize(it.id)}
                          data-testid={`platform-fiscalize-${it.id}`}
                          title="Send to vendor's easyInvoice for Albanian fiscalization"
                        >
                          <Receipt className="h-3.5 w-3.5 mr-1" />
                          {it.fiscal_status === "pending" ? "Pending…" : it.fiscal_status === "failed" ? "Retry fiscalize" : "Fiscalize"}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Auto-generate dialog */}
      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="sm:max-w-md" data-testid="platform-auto-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="h-5 w-5 text-indigo-600" /> Auto-generate platform invoice</DialogTitle>
            <DialogDescription>Rolls subscription, commission, and refund-debt into a single billing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Vendor</Label>
              <Select value={autoForm.vendor_id} onValueChange={(v) => setAutoForm((f) => ({ ...f, vendor_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="platform-auto-vendor"><SelectValue placeholder="Pick a vendor…" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.business_name || v.name || v.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Period start</Label>
                <Input type="date" value={autoForm.period_start} onChange={(e) => setAutoForm((f) => ({ ...f, period_start: e.target.value }))} className="mt-1" data-testid="platform-auto-start" />
              </div>
              <div>
                <Label>Period end</Label>
                <Input type="date" value={autoForm.period_end} onChange={(e) => setAutoForm((f) => ({ ...f, period_end: e.target.value }))} className="mt-1" data-testid="platform-auto-end" />
              </div>
            </div>
            <div className="text-xs text-neutral-600 space-y-1.5">
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={autoForm.include_subscription} onChange={(e) => setAutoForm((f) => ({ ...f, include_subscription: e.target.checked }))} /> Include subscription fees</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={autoForm.include_commission} onChange={(e) => setAutoForm((f) => ({ ...f, include_commission: e.target.checked }))} /> Include commission owed</label>
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={autoForm.include_refund_debt} onChange={(e) => setAutoForm((f) => ({ ...f, include_refund_debt: e.target.checked }))} /> Include refund debt</label>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={autoForm.notes} onChange={(e) => setAutoForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAutoOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={onAutoGenerate} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="platform-auto-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Wand2 className="h-4 w-4 mr-1.5" />} Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ad-hoc dialog */}
      <Dialog open={adhocOpen} onOpenChange={setAdhocOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="platform-adhoc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5 text-indigo-600" /> Issue ad-hoc invoice</DialogTitle>
            <DialogDescription>Compliance fines, damage disputes, or any one-off charge to a vendor.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Vendor</Label>
              <Select value={adhocForm.vendor_id} onValueChange={(v) => setAdhocForm((f) => ({ ...f, vendor_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="platform-adhoc-vendor"><SelectValue placeholder="Pick a vendor…" /></SelectTrigger>
                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.business_name || v.name || v.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="bg-neutral-50 rounded-xl p-3 space-y-2">
              {adhocForm.line_items.map((li, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-6"><Input value={li.label} onChange={(e) => onAdhocLineChange(idx, "label", e.target.value)} placeholder="Description" data-testid={`platform-adhoc-label-${idx}`} /></div>
                  <div className="col-span-1"><Input type="number" value={li.qty} onChange={(e) => onAdhocLineChange(idx, "qty", e.target.value)} className="text-right" /></div>
                  <div className="col-span-2"><Input type="number" step="0.01" value={li.unit_price} onChange={(e) => onAdhocLineChange(idx, "unit_price", e.target.value)} className="text-right" placeholder="€" data-testid={`platform-adhoc-unit-${idx}`} /></div>
                  <div className="col-span-2"><Input type="number" value={li.vat_rate} onChange={(e) => onAdhocLineChange(idx, "vat_rate", e.target.value)} className="text-right" placeholder="VAT %" /></div>
                  <div className="col-span-1 text-right">
                    {adhocForm.line_items.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => onRemoveAdhocLine(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={onAddAdhocLine} className="text-indigo-600"><Plus className="h-3.5 w-3.5 mr-1" /> Add line</Button>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={adhocForm.notes} onChange={(e) => setAdhocForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1" rows={2} placeholder="Internal context (regulation, dispute id, etc.)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdhocOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={onAdhocCreate} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="platform-adhoc-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileText className="h-4 w-4 mr-1.5" />} Issue invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
